/*
  mr-scale-calc.js — Miniature Reality scale + unit math.

  Pure functions only. No DOM, no network, no dependencies.
  Used by the scale calculator page UI, and safe to reuse on product pages,
  in an STL upload workflow, or in a Node build pipeline.

  Browser:  window.MRScaleCalc
  Node:     const MRScaleCalc = require('./mr-scale-calc.js')
*/
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  if (root) { root.MRScaleCalc = api; }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  /* ---------------------------------------------------------------- units */

  var MM_PER = {
    mm: 1,
    cm: 10,
    m: 1000,
    in: 25.4,
    ft: 304.8
  };

  var UNITS = {
    mm: { key: 'mm', label: 'millimetres', short: 'mm', mmPer: MM_PER.mm,
          toMm: function (v) { return v * MM_PER.mm; }, fromMm: function (v) { return v / MM_PER.mm; } },
    cm: { key: 'cm', label: 'centimetres', short: 'cm', mmPer: MM_PER.cm,
          toMm: function (v) { return v * MM_PER.cm; }, fromMm: function (v) { return v / MM_PER.cm; } },
    m:  { key: 'm',  label: 'metres', short: 'm', mmPer: MM_PER.m,
          toMm: function (v) { return v * MM_PER.m; },  fromMm: function (v) { return v / MM_PER.m; } },
    in: { key: 'in', label: 'inches', short: 'in', mmPer: MM_PER.in,
          toMm: function (v) { return v * MM_PER.in; }, fromMm: function (v) { return v / MM_PER.in; } },
    ft: { key: 'ft', label: 'feet', short: 'ft', mmPer: MM_PER.ft,
          toMm: function (v) { return v * MM_PER.ft; }, fromMm: function (v) { return v / MM_PER.ft; } }
  };

  var UNIT_KEYS = ['mm', 'cm', 'm', 'in', 'ft'];

  function isNum(n) { return typeof n === 'number' && isFinite(n); }

  function num(v) {
    if (typeof v === 'number') { return isFinite(v) ? v : NaN; }
    if (v === null || v === undefined) { return NaN; }
    var s = String(v).trim().replace(/,/g, '');
    if (s === '') { return NaN; }
    var n = parseFloat(s);
    return isFinite(n) ? n : NaN;
  }

  function unit(u) {
    var key = String(u || '').trim().toLowerCase();
    if (key === '"' || key === 'inch' || key === 'inches') { key = 'in'; }
    if (key === "'" || key === 'foot' || key === 'feet') { key = 'ft'; }
    if (key === 'meter' || key === 'metre' || key === 'meters' || key === 'metres') { key = 'm'; }
    return UNITS[key] || null;
  }

  function toMm(value, fromUnit) {
    var v = num(value), u = unit(fromUnit);
    if (!isNum(v) || !u) { return NaN; }
    return u.toMm(v);
  }

  function fromMm(mm, toUnit) {
    var v = num(mm), u = unit(toUnit);
    if (!isNum(v) || !u) { return NaN; }
    return u.fromMm(v);
  }

  function convert(value, fromUnit, toUnit) {
    var mm = toMm(value, fromUnit);
    if (!isNum(mm)) { return NaN; }
    return fromMm(mm, toUnit);
  }

  /* --------------------------------------------------------------- scales */

  // Scale denominator: "1:87" -> 87. Accepts 87, "87", "1:87", "1:87 (HO)", "1/87".
  function parseScale(v) {
    if (typeof v === 'number') { return isFinite(v) && v > 0 ? v : NaN; }
    if (v === null || v === undefined) { return NaN; }
    var s = String(v).trim();
    var m = s.match(/1\s*[:\/]\s*([0-9]*\.?[0-9]+)/);
    if (m) {
      var d = parseFloat(m[1]);
      return isFinite(d) && d > 0 ? d : NaN;
    }
    var n = num(s);
    return isFinite(n) && n > 0 ? n : NaN;
  }

  // CALC1 — scale to scale. factor = current / target.
  function scaleToScale(cur, target) {
    var c = parseScale(cur), t = parseScale(target);
    if (!isNum(c) || !isNum(t)) { return null; }
    var factor = c / t;
    return {
      current: c,
      target: t,
      factor: factor,
      percent: factor * 100,
      direction: factor > 1 ? 'enlarge' : (factor < 1 ? 'reduce' : 'same')
    };
  }

  // ROUND 3 item 4: the secondary way of saying the same number. A resize to
  // 181.25% is an 81.25% enlargement; 55.17% is a 44.83% reduction. This is
  // always the supporting line — the slicer percentage stays the headline.
  function deltaPercent(pct) {
    var p = num(pct);
    if (!isNum(p) || p <= 0) { return null; }
    var direction = p > 100 ? 'enlarge' : (p < 100 ? 'reduce' : 'same');
    var delta = Math.abs(p - 100);
    var label;
    if (direction === 'same') { label = 'Same size'; }
    else { label = (direction === 'enlarge' ? 'Enlarge' : 'Reduce') + ' by ' + fmtPercent(delta); }
    return { percent: p, delta: delta, direction: direction, label: label };
  }

  // CALC2 — real object to model. model = real / scale.
  // A physical dimension is a positive length: zero and negative inputs are bad
  // input, not a result, and must not reach a slicer instruction.
  function realToModel(real, unitKey, scale) {
    var mmReal = toMm(real, unitKey);
    var d = parseScale(scale);
    if (!isNum(mmReal) || mmReal <= 0 || !isNum(d)) { return null; }
    var mm = mmReal / d;
    return { mm: mm, in: mm / MM_PER.in, cm: mm / MM_PER.cm, m: mm / MM_PER.m, ft: mm / MM_PER.ft };
  }

  // Inverse of realToModel — real = model * scale.
  function modelToReal(model, unitKey, scale) {
    var mmModel = toMm(model, unitKey);
    var d = parseScale(scale);
    if (!isNum(mmModel) || mmModel <= 0 || !isNum(d)) { return null; }
    var mm = mmModel * d;
    return { mm: mm, in: mm / MM_PER.in, cm: mm / MM_PER.cm, m: mm / MM_PER.m, ft: mm / MM_PER.ft };
  }

  // CALC3 — what slicer % turns this STL dimension into the right scale size?
  function requiredSlicerPercent(stlDim, stlUnit, realDim, realUnit, scale) {
    var stlMm = toMm(stlDim, stlUnit);
    var target = realToModel(realDim, realUnit, scale);
    if (!isNum(stlMm) || stlMm <= 0 || !target) { return null; }
    return {
      currentStlMm: stlMm,
      currentStlIn: stlMm / MM_PER.in,
      desiredModelMm: target.mm,
      desiredModelIn: target.in,
      percent: (target.mm / stlMm) * 100,
      factor: target.mm / stlMm
    };
  }

  // CALC4 — estimate an unknown scale. denominator = real / model (same units).
  function estimateScale(realSize, realUnit, modelSize, modelUnit) {
    var r = toMm(realSize, realUnit);
    var m = toMm(modelSize, modelUnit);
    if (!isNum(r) || r <= 0 || !isNum(m) || m <= 0) { return null; }
    var denominator = r / m;
    return { denominator: denominator, label: '1:' + fmtDenominator(denominator) };
  }

  // CALC4b — pixel reference. Real size of a target measured in pixels, using a
  // reference object of known real size measured in the same image.
  function pixelReference(refRealSize, refUnit, refPx, targetPx) {
    var refMm = toMm(refRealSize, refUnit);
    var rp = num(refPx), tp = num(targetPx);
    if (!isNum(refMm) || refMm <= 0 || !isNum(rp) || rp <= 0 || !isNum(tp) || tp <= 0) { return null; }
    var mm = (tp / rp) * refMm;
    return {
      mm: mm,
      cm: mm / MM_PER.cm,
      m: mm / MM_PER.m,
      in: mm / MM_PER.in,
      ft: mm / MM_PER.ft,
      pxRatio: tp / rp
    };
  }

  /* -------------------------------------------------------------- presets */

  // group: 'common' shows as a chip straight away, 'more' hides behind the
  // "More scales" disclosure, 'table' appears in the reference table only.
  var PRESETS = [
    { denom: 12,    name: '',   label: '1:12',   group: 'common', note: 'Dollhouse "one inch" scale' },
    { denom: 18,    name: '',   label: '1:18',   group: 'more',   note: 'Large diecast vehicles' },
    { denom: 20,    name: '',   label: '1:20',   group: 'table',  note: '' },
    { denom: 22.5,  name: 'G',  label: '1:22.5', group: 'more',   note: 'G scale garden railways — G covers a range of ratios, not one standard', nameSuffix: 'varies' },
    { denom: 24,    name: '',   label: '1:24',   group: 'common', note: 'Half-inch dollhouse, model cars' },
    { denom: 25,    name: '',   label: '1:25',   group: 'table',  note: '' },
    { denom: 32,    name: '',   label: '1:32',   group: 'more',   note: 'Larger gaming figures and diecast' },
    { denom: 35,    name: '',   label: '1:35',   group: 'common', note: 'Military models and dioramas' },
    { denom: 43,    name: '',   label: '1:43',   group: 'more',   note: 'Diecast cars; British O is about 1:43.5' },
    { denom: 48,    name: 'O',  label: '1:48',   group: 'common', note: 'O scale (US), quarter-inch. European O is about 1:45' },
    { denom: 50,    name: '',   label: '1:50',   group: 'more',   note: 'Construction models' },
    { denom: 56,    name: '',   label: '1:56',   group: 'common', note: 'Commonly associated with 28mm gaming figures' },
    { denom: 64,    name: '',   label: '1:64',   group: 'common', note: 'S scale, and many 28mm ranges' },
    { denom: 72,    name: '',   label: '1:72',   group: 'common', note: 'Aircraft and small-scale military' },
    { denom: 76,    name: 'OO', label: '1:76',   group: 'more',   note: 'OO scale (UK), runs on HO track' },
    { denom: 87.1,  name: 'HO', label: '1:87',   group: 'common', note: 'HO scale — the most common model railroad scale. The exact ratio is 1:87.1; 1:87 is the everyday shorthand' },
    { denom: 100,   name: '',   label: '1:100',  group: 'more',   note: 'Architectural models' },
    { denom: 120,   name: 'TT', label: '1:120',  group: 'more',   note: 'TT scale' },
    { denom: 144,   name: '',   label: '1:144',  group: 'more',   note: '' },
    { denom: 160,   name: 'N',  label: '1:160',  group: 'common', note: 'N scale (US/Europe). British N is about 1:148' },
    { denom: 220,   name: 'Z',  label: '1:220',  group: 'more',   note: 'Z scale' }
  ];

  // Beginner-friendly footnotes for the scales that are not one single ratio.
  var SCALE_NAMING_NOTES = [
    'HO is 1:87.1 exactly; almost everyone writes it 1:87, and either number gives the same answer to within a tenth of a percent.',
    'O scale is 1:48 in the US, about 1:43.5 in Britain and about 1:45 in Europe.',
    'N scale is 1:160 in the US and Europe, about 1:148 in Britain.',
    'G is a family of garden-railway scales, not one ratio — 1:22.5 is the common one, but 1:20.3, 1:24 and 1:29 all get called G.'
  ];

  // 28mm / 32mm figure "scales" are not one exact ratio — they describe a figure
  // height measured by different conventions, and every manufacturer differs.
  var FIGURE_SCALE_NOTE =
    '28mm and 32mm are figure sizes, not exact scales. Manufacturers measure to ' +
    'the eyes, the top of the head, or the hat, so the same "28mm" range can work ' +
    'out anywhere from roughly 1:56 to 1:64. Measure a figure you already own if ' +
    'you need a close match.';

  // "1:87 HO", "1:48 O", "1:22.5 (G, varies)", "1:35"
  function presetLabel(p) {
    if (!p) { return ''; }
    if (p.name && p.nameSuffix) { return p.label + ' (' + p.name + ', ' + p.nameSuffix + ')'; }
    return p.name ? (p.label + ' ' + p.name) : p.label;
  }

  function presetsInGroup(group) {
    return PRESETS.filter(function (p) { return p.group === group; });
  }

  /* ------------------------------------------------- browse-by-scale links */

  // Where a finished result should send someone who wants models at that scale.
  // Ranges, not exact matches, so 1:87 and 1:87.1 both land on HO.
  // `name` is the hobby name used in the prompt where one applies (HO / N / O).
  var SCALE_BROWSE = [
    { min: 85,   max: 89,   name: 'HO', label: 'Browse 3D-printable HO models',        url: '/collections/model-railroading' },
    { min: 145,  max: 165,  name: 'N',  label: 'Browse N scale models',                url: '/collections/model-railroading' },
    { min: 47,   max: 49,   name: 'O',  label: 'Browse O scale models',                url: '/collections/model-railroading' },
    { min: 30,   max: 33,   name: '',   label: 'Browse tabletop & wargaming models',   url: '/collections/wargaming' },
    { min: 54,   max: 58,   name: '',   label: 'Browse tabletop & wargaming models',   url: '/collections/wargaming' },
    { min: 70,   max: 74,   name: '',   label: 'Browse tabletop & wargaming models',   url: '/collections/wargaming' },
    { min: 11.5, max: 12.5, name: '',   label: 'Browse dollhouse-scale models',        url: '/collections/dollhouses' },
    { min: 23,   max: 25,   name: '',   label: 'Browse dollhouse-scale models',        url: '/collections/dollhouses' }
  ];

  // ROUND 3 item 8: no generic fallback. A scale with no room of its own returns
  // null, and the UI hides the link rather than sending someone somewhere vague.
  function browseFor(denom) {
    var d = parseScale(denom);
    if (!isNum(d)) { return null; }
    for (var i = 0; i < SCALE_BROWSE.length; i++) {
      var row = SCALE_BROWSE[i];
      if (d >= row.min && d <= row.max) {
        var scaleText = fmtScale(d);
        var named = row.name ? scaleText + ' (' + row.name + ')' : scaleText;
        return {
          url: row.url,
          matched: true,
          denom: d,
          name: row.name || '',
          scaleText: scaleText,
          // Link text carries the visitor's own scale.
          label: 'Browse ' + scaleText + ' models',
          prompt: 'Looking for models already made at ' + named + '?',
          roomLabel: row.label
        };
      }
    }
    return null;
  }

  var CONFIDENCE = {
    exact:    'Extremely close',
    likely:   'Likely match',
    possible: 'Possible match',
    custom:   'Probably a custom/non-standard scale'
  };

  function confidenceFor(diffPercent) {
    if (diffPercent < 1) { return CONFIDENCE.exact; }
    if (diffPercent <= 3) { return CONFIDENCE.likely; }
    if (diffPercent <= 5) { return CONFIDENCE.possible; }
    return CONFIDENCE.custom;
  }

  // Nearest common hobby scale to a derived denominator.
  // diffPercent is measured against the preset: |preset - derived| / preset * 100.
  function closestPreset(denom) {
    var d = parseScale(denom);
    if (!isNum(d)) { return null; }
    var best = null, bestDiff = Infinity;
    for (var i = 0; i < PRESETS.length; i++) {
      var diff = Math.abs(PRESETS[i].denom - d) / PRESETS[i].denom * 100;
      if (diff < bestDiff) { bestDiff = diff; best = PRESETS[i]; }
    }
    return {
      preset: best,
      diffPercent: bestDiff,
      confidence: confidenceFor(bestDiff),
      isExactPreset: bestDiff === 0
    };
  }

  /* -------------------------------------------------------------- slicers */

  // Single source of truth for slicer-specific guidance.
  //   instruction  short steps; {pct} is replaced with the calculated percentage
  //   docs         official vendor documentation (each URL verified live)
  //   video        official vendor video, or null — the UI hides the button then
  //   warnings     standing caveats for that slicer, shown with any result
  var SLICERS = [
    {
      key: 'bambu', name: 'Bambu Studio', type: 'fdm',
      instruction: 'Select model → Scale → Uniform Scale ON → enter {pct}%',
      docs: 'https://wiki.bambulab.com/en/bambu-studio/skills',
      video: null,
      warnings: ['Scaling changes the model only — layer height, wall count and nozzle stay where your print profile put them.']
    },
    {
      key: 'orca', name: 'OrcaSlicer', type: 'fdm',
      instruction: 'Select model → Scale tool → Uniform Scale ON → enter {pct}% in the Scale row',
      docs: 'https://github.com/OrcaSlicer/OrcaSlicer/wiki/prepare_object_manipulation',
      video: null,
      warnings: ['The Scale row takes a percentage; the Size row below it takes millimetres. Either works — do not set both.']
    },
    {
      key: 'prusa', name: 'PrusaSlicer', type: 'fdm',
      instruction: 'Select model → press S → Scale factor → enter {pct}% on all three axes',
      docs: 'https://help.prusa3d.com/article/move-rotate-scale-tools_1914',
      video: null,
      warnings: ['The reset arrow beside Scale factor puts the object back to 100% if you want to start again.']
    },
    {
      key: 'cura', name: 'UltiMaker Cura', type: 'fdm',
      instruction: 'Select model → Scale tool → Uniform Scaling ON → enter {pct} in the percentage box',
      docs: 'https://support.makerbot.com/s/article/1667410777359',
      video: null,
      warnings: ['With Uniform Scaling ticked, typing the percentage into one box fills the other two.']
    },
    {
      key: 'lychee', name: 'Lychee Slicer', type: 'resin',
      instruction: 'Select model → Scale panel (right) → Uniform Scaling ON → enter {pct}%',
      docs: 'https://sites.google.com/mango3d.io/lycheesliceredu/layout',
      // Official Lychee channel (@Lychee3d), "…before scaling down a model".
      video: 'https://www.youtube.com/watch?v=X9DsHAG39fI',
      warnings: ['Scaling a model that already carries supports scales the supports along with it.']
    },
    {
      key: 'chitubox', name: 'CHITUBOX', type: 'resin',
      instruction: 'Select model → Scale tool → Lock ratio ON → enter {pct}%',
      docs: 'https://docs.chitubox.com/en-US/chitubox-basic/v1.9.5/application-overview/ui-introduction',
      // Official CHITUBOX channel (@chitubox_official), Move/Rotate/Scale/Mirror.
      video: 'https://www.youtube.com/watch?v=EmGABeAby4o',
      warnings: ['Lock ratio has to be on, or X, Y and Z scale independently.']
    }
  ];

  var SLICER_KEYS = SLICERS.map(function (s) { return s.key; });

  function getSlicer(key) {
    if (!key) { return null; }
    var k = String(key).trim().toLowerCase();
    for (var i = 0; i < SLICERS.length; i++) {
      if (SLICERS[i].key === k) { return SLICERS[i]; }
    }
    return null;
  }

  function instructionFor(key, pct) {
    var s = getSlicer(key);
    if (!s) { return ''; }
    return s.instruction.replace('{pct}', fmtPercent(pct, { bare: true }));
  }

  // How big a resize is this? Guidance only — nothing here blocks a calculation.
  var SEVERITY_ORDER = ['minor', 'moderate', 'substantial', 'major'];
  var SEVERITY_LABELS = {
    minor: 'Minor resize',
    moderate: 'Moderate resize',
    substantial: 'Substantial resize',
    major: 'Major resize'
  };

  function resizeSeverity(pct) {
    var p = num(pct);
    if (!isNum(p) || p <= 0) { return null; }
    var level;
    if (p >= 90 && p <= 110) { level = 'minor'; }
    else if ((p >= 70 && p < 90) || (p > 110 && p <= 140)) { level = 'moderate'; }
    else if ((p >= 50 && p < 70) || (p > 140 && p <= 200)) { level = 'substantial'; }
    else { level = 'major'; }
    return {
      percent: p,
      level: level,
      label: SEVERITY_LABELS[level],
      rank: SEVERITY_ORDER.indexOf(level),
      direction: p > 100 ? 'up' : (p < 100 ? 'down' : 'none')
    };
  }

  function severityAtLeast(sev, level) {
    return !!sev && sev.rank >= SEVERITY_ORDER.indexOf(level);
  }

  var RESIN_WARNING =
    'Scaling a pre-supported model can change: support contact size, support shaft ' +
    'thickness, wall thickness, hollowing, drainage holes, tolerances. Check or ' +
    'rebuild supports after significant resizing.';

  var FDM_WARNING =
    'The calculated scale may be geometrically correct while small walls, pins, ' +
    'holes and details become too small for the nozzle or layer height.';

  // Warnings for a slicer at a given resize percentage. Advisory, never blocking.
  function warningsFor(slicerKey, pct) {
    var s = getSlicer(slicerKey);
    var sev = resizeSeverity(pct);
    var list = [];
    if (!s || !sev) { return list; }

    if (s.type === 'resin' && severityAtLeast(sev, 'substantial')) {
      var strong = sev.direction === 'down';
      list.push({
        id: 'resin',
        level: strong ? 'strong' : 'warn',
        title: strong ? 'Resin model warning — major change' : 'Resin model warning',
        text: RESIN_WARNING
      });
    }

    if (s.type === 'fdm' && sev.direction === 'down') {
      if (severityAtLeast(sev, 'substantial')) {
        list.push({ id: 'fdm', level: 'warn', title: 'Printability check', text: FDM_WARNING });
      } else if (sev.level === 'moderate') {
        list.push({
          id: 'fdm-mild', level: 'info', title: 'Printability note',
          text: 'A reduction this size is usually fine, but check that the thinnest walls and smallest pins still clear your nozzle diameter.'
        });
      }
    }

    return list;
  }

  /* --------------------------------------------------------- printability */

  // Typical consumer machines. These two pairs of numbers are the ONLY
  // thresholds the printability check uses — change them here and every table,
  // verdict line and test follows.
  //   maxMm  largest overall dimension the bed/plate takes
  //   minMm  smallest overall dimension that still prints as a recognisable
  //          object (0.4 mm nozzle on FDM, ~50 micron resin)
  var PRINTERS = {
    fdm:   { name: 'FDM',   maxMm: 220, minMm: 4 },
    resin: { name: 'Resin', maxMm: 150, minMm: 1.5 }
  };

  var PRINT_TECHS = ['fdm', 'resin'];

  function techReason(tech, verdict) {
    var p = PRINTERS[tech];
    if (verdict === 'too-large') {
      return tech === 'fdm'
        ? 'Too large for a typical FDM bed (' + p.maxMm + ' mm)'
        : 'Too large for a typical resin plate (' + p.maxMm + ' mm)';
    }
    if (verdict === 'too-small') {
      return tech === 'fdm'
        ? 'Too small for FDM detail (under ' + p.minMm + ' mm)'
        : 'Too small for resin detail (under ' + p.minMm + ' mm)';
    }
    return '';
  }

  // dimsMm: one number, or an array of 1-3 model dimensions in millimetres.
  // The largest dimension is checked against the bed, the smallest against the
  // detail floor. Advisory only — nothing in the UI blocks on this.
  function printability(dimsMm) {
    var list = (dimsMm instanceof Array ? dimsMm : [dimsMm])
      .map(num)
      .filter(function (v) { return isNum(v) && v > 0; });
    if (!list.length) { return null; }

    var largest = Math.max.apply(null, list);
    var smallest = Math.min.apply(null, list);

    var result = { dimsMm: list, largestMm: largest, smallestMm: smallest };
    for (var i = 0; i < PRINT_TECHS.length; i++) {
      var tech = PRINT_TECHS[i], p = PRINTERS[tech];
      var verdict = largest > p.maxMm ? 'too-large' : (smallest < p.minMm ? 'too-small' : 'ok');
      result[tech] = verdict;
      result[tech + 'Reason'] = techReason(tech, verdict);
    }

    var fdmOk = result.fdm === 'ok', resinOk = result.resin === 'ok';
    if (fdmOk && resinOk) {
      result.summary = 'both'; result.label = 'FDM + Resin'; result.tone = 'ok'; result.reason = '';
    } else if (fdmOk) {
      result.summary = 'fdm'; result.label = 'FDM only'; result.tone = 'warn';
      result.reason = result.resinReason;
    } else if (resinOk) {
      result.summary = 'resin'; result.label = 'Resin only'; result.tone = 'warn';
      result.reason = result.fdmReason;
    } else {
      result.summary = 'no'; result.label = 'NO'; result.tone = 'bad';
      if (result.fdm === 'too-large' && result.resin === 'too-large') {
        result.reason = 'Too large for both; split the model or print it in sections';
      } else if (result.fdm === 'too-small' && result.resin === 'too-small') {
        result.reason = 'Too small to print as a recognisable object on either';
      } else {
        result.reason = result.fdmReason + '; ' + result.resinReason.toLowerCase();
      }
    }
    return result;
  }

  var PRINTABILITY_NOTE =
    'Based on a typical ' + PRINTERS.fdm.maxMm + ' mm FDM bed and ' +
    PRINTERS.resin.maxMm + ' mm resin plate; bigger parts can be split or tilted, ' +
    'and dedicated large-format machines go further.';

  /* ----------------------------------------------------- quick reference */

  // One real-world measurement -> model size at every preset.
  function quickReference(realSize, unitKey) {
    var mm = toMm(realSize, unitKey);
    if (!isNum(mm) || mm <= 0) { return []; }
    return PRESETS.map(function (p) {
      var modelMm = mm / p.denom;
      return {
        denom: p.denom,
        label: p.label,
        name: p.name,
        note: p.note,
        display: presetLabel(p),
        mm: modelMm,
        in: modelMm / MM_PER.in
      };
    });
  }

  /* ----------------------------------------------------------- formatting */

  function round(n, dp) {
    var f = Math.pow(10, dp);
    return Math.round((n + Number.EPSILON) * f) / f;
  }

  function trimZeros(s) {
    if (s.indexOf('.') === -1) { return s; }
    return s.replace(/0+$/, '').replace(/\.$/, '');
  }

  function fmtMm(mm, opts) {
    var v = num(mm);
    if (!isNum(v)) { return '—'; }
    var dp = Math.abs(v) < 1 ? 3 : 2;
    var s = trimZeros(v.toFixed(dp));
    return (opts && opts.bare) ? s : s + ' mm';
  }

  function fmtIn(inches, opts) {
    var v = num(inches);
    if (!isNum(v)) { return '—'; }
    var dp = Math.abs(v) < 1 ? 3 : 2;
    var s = trimZeros(v.toFixed(dp));
    return (opts && opts.bare) ? s : s + ' in';
  }

  function fmtPercent(pct, opts) {
    var v = num(pct);
    if (!isNum(v)) { return '—'; }
    var s = trimZeros(v.toFixed(2));
    return (opts && opts.bare) ? s : s + '%';
  }

  // 438.095 in -> "36 ft 6.1 in"
  function fmtFeetInches(inches) {
    var v = num(inches);
    if (!isNum(v)) { return '—'; }
    var sign = v < 0 ? '-' : '';
    v = Math.abs(v);
    var ft = Math.floor(v / 12);
    var rem = v - ft * 12;
    if (round(rem, 1) >= 12) { ft += 1; rem = 0; }
    var remStr = trimZeros(rem.toFixed(1));
    if (ft === 0) { return sign + remStr + ' in'; }
    if (parseFloat(remStr) === 0) { return sign + ft + ' ft'; }
    return sign + ft + ' ft ' + remStr + ' in';
  }

  // Scale denominators print without pointless decimals: 87 not 87.00, 22.5 kept.
  function fmtDenominator(d) {
    var v = num(d);
    if (!isNum(v)) { return '—'; }
    if (Math.abs(v - Math.round(v)) < 0.005) { return String(Math.round(v)); }
    return trimZeros(v.toFixed(2));
  }

  function fmtScale(d) { return '1:' + fmtDenominator(d); }

  /* --------------------------------------------------- share-state helpers */

  // Keys kept short and stable so shared links stay readable:
  //   mode, from, to, real, ru (real unit), scale, stl, su, rdim, rdu,
  //   ref, refu, meas, measu, dpx, bpx, q, qu
  var STATE_KEYS = ['mode', 'from', 'to', 'real', 'ru', 'scale', 'stl', 'su',
                    'rdim', 'rdu', 'tscale', 'ref', 'refu', 'meas', 'measu',
                    'prf', 'prfu', 'dpx', 'bpx', 'bscale', 'q', 'qu',
                    'slicer', 'src'];

  // The four tasks the page offers, and the only params each one shares.
  var MODES = ['resize', 'dimensions', 'identify', 'photo'];

  var MODE_KEYS = {
    resize:     ['from', 'to', 'stl', 'su', 'rdim', 'rdu', 'tscale'],
    dimensions: ['real', 'ru', 'scale'],
    identify:   ['ref', 'refu', 'meas', 'measu'],
    photo:      ['prf', 'prfu', 'dpx', 'bpx', 'bscale']
  };

  // Accepts the short names, the round-1 internal names, and a few obvious
  // synonyms, so old links and product links keep working.
  var MODE_ALIASES = {
    scale: 'resize', resize: 'resize', 'scale-to-scale': 'resize', 'stl-to-percent': 'resize',
    dimensions: 'dimensions', 'real-to-model': 'dimensions', real: 'dimensions',
    identify: 'identify', 'estimate-scale': 'identify', unknown: 'identify',
    photo: 'photo', 'image-reference': 'photo', image: 'photo'
  };

  function normalizeMode(mode) {
    var m = String(mode || '').trim().toLowerCase();
    return MODE_ALIASES[m] || '';
  }

  // ROUND 3 item 14: internally the resize tab is 'resize'; shared links say
  // 'scale-to-scale', which is what the owner's example URLs use. Both are
  // accepted on the way in (MODE_ALIASES above); this is the way out.
  var MODE_SHARE_NAMES = {
    resize: 'scale-to-scale', dimensions: 'dimensions',
    identify: 'identify', photo: 'photo'
  };

  function shareModeName(mode) {
    var m = normalizeMode(mode) || 'resize';
    return MODE_SHARE_NAMES[m] || m;
  }

  // Build the share query for one mode: mode + that mode's params (+ slicer).
  // Nothing from the other three calculators travels in the link.
  function shareQuery(mode, state) {
    var m = normalizeMode(mode) || 'resize';
    var keys = MODE_KEYS[m] || [];
    var picked = { mode: shareModeName(m) };
    for (var i = 0; i < keys.length; i++) {
      var v = state ? state[keys[i]] : '';
      if (v !== undefined && v !== null && String(v) !== '') { picked[keys[i]] = v; }
    }
    if (state && state.slicer) { picked.slicer = state.slicer; }
    return stateToQuery(picked);
  }

  function stateToQuery(state) {
    if (!state) { return ''; }
    var parts = [];
    for (var i = 0; i < STATE_KEYS.length; i++) {
      var k = STATE_KEYS[i];
      var v = state[k];
      if (v === undefined || v === null || v === '') { continue; }
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(v)));
    }
    return parts.join('&');
  }

  function queryToState(query) {
    var out = {};
    if (!query) { return out; }
    var s = String(query);
    var qIdx = s.indexOf('?');
    if (qIdx >= 0) { s = s.slice(qIdx + 1); }
    s = s.replace(/^[?#]/, '');
    if (!s) { return out; }
    var pairs = s.split('&');
    for (var i = 0; i < pairs.length; i++) {
      if (!pairs[i]) { continue; }
      var eq = pairs[i].indexOf('=');
      var k = eq === -1 ? pairs[i] : pairs[i].slice(0, eq);
      var v = eq === -1 ? '' : pairs[i].slice(eq + 1);
      try { k = decodeURIComponent(k); } catch (e) {}
      try { v = decodeURIComponent(v.replace(/\+/g, ' ')); } catch (e2) {}
      if (STATE_KEYS.indexOf(k) >= 0) { out[k] = v; }
    }
    return out;
  }

  /* ------------------------------------------------------- reference sizes */

  // Everyday objects useful for estimating an unknown scale. These are typical
  // sizes, not standards — treat them as estimates unless you know the prototype.
  var REFERENCES = [
    { key: 'door',       label: 'Residential door height', size: 80, unit: 'in' },
    { key: 'doorway',    label: 'Doorway width',           size: 32, unit: 'in' },
    { key: 'human',      label: 'Adult human height',      size: 69, unit: 'in' },
    { key: 'car',        label: 'Mid-size car length',     size: 190, unit: 'in' },
    { key: 'wheelbase',  label: 'Pickup truck wheelbase',  size: 145, unit: 'in' },
    { key: 'gauge',      label: 'Standard railroad gauge', size: 56.5, unit: 'in' },
    { key: 'container',  label: '20 ft shipping container length', size: 20, unit: 'ft' },
    { key: 'lumber',     label: 'Nominal 2x4 (actual 3.5 in wide)', size: 3.5, unit: 'in' },
    { key: 'step',       label: 'Stair riser height',      size: 7.5, unit: 'in' },
    { key: 'brick',      label: 'Standard brick length',   size: 8, unit: 'in' }
  ];

  return {
    UNITS: UNITS,
    UNIT_KEYS: UNIT_KEYS,
    MM_PER: MM_PER,
    PRESETS: PRESETS,
    REFERENCES: REFERENCES,
    SLICERS: SLICERS,
    SLICER_KEYS: SLICER_KEYS,
    SEVERITY_ORDER: SEVERITY_ORDER,
    RESIN_WARNING: RESIN_WARNING,
    FDM_WARNING: FDM_WARNING,
    getSlicer: getSlicer,
    instructionFor: instructionFor,
    resizeSeverity: resizeSeverity,
    severityAtLeast: severityAtLeast,
    warningsFor: warningsFor,
    PRINTERS: PRINTERS,
    PRINTABILITY_NOTE: PRINTABILITY_NOTE,
    printability: printability,
    CONFIDENCE: CONFIDENCE,
    FIGURE_SCALE_NOTE: FIGURE_SCALE_NOTE,

    num: num,
    parseScale: parseScale,
    toMm: toMm,
    fromMm: fromMm,
    convert: convert,

    scaleToScale: scaleToScale,
    realToModel: realToModel,
    modelToReal: modelToReal,
    requiredSlicerPercent: requiredSlicerPercent,
    estimateScale: estimateScale,
    pixelReference: pixelReference,
    closestPreset: closestPreset,
    confidenceFor: confidenceFor,
    presetLabel: presetLabel,
    presetsInGroup: presetsInGroup,
    quickReference: quickReference,
    SCALE_BROWSE: SCALE_BROWSE,
    deltaPercent: deltaPercent,
    SCALE_NAMING_NOTES: SCALE_NAMING_NOTES,
    browseFor: browseFor,
    MODES: MODES,
    MODE_KEYS: MODE_KEYS,
    normalizeMode: normalizeMode,
    shareModeName: shareModeName,
    shareQuery: shareQuery,

    round: round,
    fmtMm: fmtMm,
    fmtIn: fmtIn,
    fmtPercent: fmtPercent,
    fmtFeetInches: fmtFeetInches,
    fmtDenominator: fmtDenominator,
    fmtScale: fmtScale,

    STATE_KEYS: STATE_KEYS,
    stateToQuery: stateToQuery,
    queryToState: queryToState
  };
});
